import React from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, Refrigerator, Clock, Truck, Gift } from "lucide-react";
import { useCustomer } from "@/context/CustomerContext";

const HomePage = () => {
  const navigate = useNavigate();
  const { customer, isLoggedIn } = useCustomer();
  const isBusiness = isLoggedIn && customer?.account_type === "business" && !customer?.partner_company_id;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* Promo Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 py-3 px-4" data-testid="promo-banner">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-3">
          <Gift className="w-5 h-5 text-black animate-pulse" />
          <p className="text-black font-bold text-sm sm:text-base text-center">
            {isBusiness 
              ? "ORDINE MINIMO 50€ - Spedizione GRATUITA!" 
              : "Spedizione GRATUITA per ordini sopra 50€!"
            }
          </p>
          <Gift className="w-5 h-5 text-black animate-pulse" />
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-20 px-4" data-testid="hero-section">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6" data-testid="hero-title">
            PREGIATO
          </h1>
          <p className="text-lg sm:text-xl text-zinc-300 mb-8 max-w-2xl mx-auto" data-testid="hero-subtitle">
            Piatti pronti monoporzione, freschi da frigo e consegnati a casa tua.
          </p>
          <button
            onClick={() => navigate("/catalogo")}
            className="bg-green-500 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-600 transition-all inline-flex items-center gap-2"
            data-testid="order-now-button"
          >
            Ordina ora
            <ArrowRight className="w-5 h-5" />
          </button>
          {!isLoggedIn && (
            <div className="mt-8 bg-gradient-to-r from-green-500/20 to-yellow-500/20 border border-green-500/50 rounded-xl p-6 max-w-md mx-auto" data-testid="register-banner">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-black" />
                </div>
                <p className="text-yellow-500 font-bold text-lg">20 punti omaggio!</p>
              </div>
              <p className="text-zinc-300 text-sm mb-4">Registrati ora e ricevi subito <strong className="text-white">20 punti omaggio</strong> per iniziare ad accumulare premi.</p>
              <button
                onClick={() => navigate("/accedi?tab=register")}
                className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-all inline-flex items-center gap-2 w-full justify-center"
                data-testid="register-cta-button"
              >
                Registrati e accumula punti
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-zinc-950" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6" data-testid="feature-fresh">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
                <Refrigerator className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Freschezza garantita</h3>
              <p className="text-zinc-400">
                Tutti i piatti sono freschi da frigo e preparati con ingredienti selezionati.
              </p>
            </div>
            <div className="text-center p-6" data-testid="feature-duration">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500 rounded-full mb-4">
                <Clock className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Lunga conservazione</h3>
              <p className="text-zinc-400">
                La durata varia tra 15 e 45 giorni a seconda del prodotto.
              </p>
            </div>
            <div className="text-center p-6" data-testid="feature-delivery">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-4">
                <Truck className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {isBusiness ? "Consegna presso la tua attività" : "Consegna a casa"}
              </h3>
              <p className="text-zinc-400">
                Scegli giorno e fascia oraria per ricevere i tuoi piatti refrigerati.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-4" data-testid="info-section">
        <div className="max-w-4xl mx-auto">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-white mb-4" data-testid="info-title">
              Freschezza garantita
            </h2>
            <p className="text-zinc-300 text-lg leading-relaxed" data-testid="info-text">
              Tutti i piatti PREGIATO sono freschi da frigo, preparati con ingredienti
              selezionati e confezionati in modo sicuro. La durata varia tra 15 e 45
              giorni a seconda del prodotto.
            </p>
          </div>
        </div>
      </section>

      {/* Loyalty Program Banner - Solo per non-business */}
      {!isBusiness && (
        <section className="py-8 px-4" data-testid="loyalty-banner">
          <div className="max-w-4xl mx-auto">
            <div 
              className="bg-gradient-to-r from-yellow-500/20 to-green-500/20 border border-yellow-500/50 rounded-xl p-6 cursor-pointer hover:border-yellow-500 transition-colors"
              onClick={() => navigate("/programma-fedelta")}
            >
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-14 h-14 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-7 h-7 text-black" />
                </div>
                <div className="text-center sm:text-left flex-1">
                  <h3 className="text-xl font-bold text-white mb-1">
                    Programma Fedeltà
                  </h3>
                  <p className="text-zinc-400">
                    Accumula punti con ogni ordine! <span className="text-yellow-500 font-semibold">300 punti = 1 piatto gratis</span>
                  </p>
                </div>
                <button className="bg-yellow-500 text-black px-4 py-2 rounded-lg font-bold hover:bg-yellow-400 transition-colors flex items-center gap-2">
                  Scopri di più
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
